import moment, { type Moment } from 'moment'
import flatten from 'lodash/flatten'
import {
  type ShiftsApiResponseStaffingLevels,
  AllocationType,
} from 'domains/shifts/components/types'
import { addInterval, INTERVALS_PER_HOUR } from 'domains/shifts/components/utils/intervalUtils'
import { startOfCurrentHour } from 'legacy/common/typedDateUtils'
import { type Shift, ShiftType, type ShopperWithShifts } from 'domains/shifts/components/types'
import {
  type StaffingLevel,
  type WarehouseLocationHours,
} from 'domains/shifts/components/components/staffing-levels/utils/types'

/* eslint-disable @typescript-eslint/naming-convention */

/**
 * Returns an object of shift allocations
 * keys are combination of timestamp of the hour + shift type + allocation type (iff device prioritization enabled)
 * values are the # of shifts for that hour + shift type + allocation type
 */
const computeShiftAllocations = ({
  shifts,
  useDevicePrioritization,
  isRunnerDevicePrioritizationEnabled,
}: {
  shifts: Shift[]
  useDevicePrioritization: boolean
  isRunnerDevicePrioritizationEnabled: boolean
}) => {
  const allocations: {
    [key: string]: number
  } = {}

  shifts.forEach(shift => {
    // skip shifts that are added as placeholder for the shift edit modal
    if (shift.placeholder) {
      return
    }

    const end = new Date(shift.ends_at)
    let current = new Date(shift.starts_at)

    while (current < end) {
      // Staffing allocations are collected by the hour
      // each interval adds a portion of staffing to that hour
      // ex: if a shift is active during the interval 9:15 - 9:30 that will add 0.25 allocated staffing to the 9:00 - 10:00 staffing window
      const timeString = startOfCurrentHour(current).getTime()
      let key = timeString + shift.shift_type

      if (useDevicePrioritization && shift.shift_type === ShiftType.Picking) {
        // if device prioritization is enabled and this is a picking shift, we also key shifts allocations by dedicated/on_call shift types
        key += shift.on_call ? AllocationType.OnCall : AllocationType.Dedicated
      }

      if (isRunnerDevicePrioritizationEnabled && shift.shift_type === ShiftType.Running) {
        // if runner device prioritization is enabled and this is a running shift, we also key shifts allocations by dedicated/on_call shift types
        key += shift.on_call ? AllocationType.OnCall : AllocationType.Dedicated
      }

      allocations[key] = (allocations[key] || 0) + 1 / INTERVALS_PER_HOUR
      current = addInterval(current, 1)
    }
  })

  return allocations
}

/**
 * Returns a count of scheduled shifts using the startTime of the hour, shiftType, and shiftAllocationType
 */
const getScheduledCount = ({
  shiftAllocations,
  startTime,
  shiftType,
  shiftAllocationType,
}: {
  shiftAllocations: ReturnType<typeof computeShiftAllocations>
  startTime: Moment
  shiftType: ShiftType
  shiftAllocationType?: AllocationType
}): number => {
  let key = startTime.toDate().getTime().toString() + shiftType

  if (shiftAllocationType) {
    key += shiftAllocationType
  }

  return shiftAllocations[key] || 0
}

/**
 * Returns a count of required shifts by parsing the staffing level value sent from the server
 */
const getRequiredCount: (value: string) => number | null = value => {
  const parsed = parseFloat(value)
  return isNaN(parsed) ? null : parsed
}

/**
 * Combines the staffing level recommendations with the shopper allocations and delivery hours [store hours].
 * For each store hour, [eg. 9am - 10am] it will try to apply the corresponding staffing levels for that hour.
 * @param staffingLevels Staffing levels from the API response
 * @param shoppers Shopper with scheduled shifts
 * @param storeHours The store's delivery hours
 * @returns
 */
export const computeStaffingLevels = ({
  staffingLevels,
  shoppers,
  storeHours,
  useDevicePrioritization,
  isRunnerDevicePrioritizationEnabled,
}: {
  staffingLevels: ShiftsApiResponseStaffingLevels[]
  shoppers: ShopperWithShifts[]
  storeHours: WarehouseLocationHours
  useDevicePrioritization: boolean
  isRunnerDevicePrioritizationEnabled: boolean
}): StaffingLevel[] => {
  const staffingAllocation: StaffingLevel[] = []

  if (!storeHours) return []

  const { actualStoreOpen, actualStoreClose } = storeHours
  const endTime = moment(actualStoreClose)
  const incrementalTime = moment(actualStoreOpen)

  const shifts = flatten(shoppers?.map(shopper => shopper.shifts))
  const shiftAllocations = computeShiftAllocations({
    shifts,
    useDevicePrioritization,
    isRunnerDevicePrioritizationEnabled,
  })

  while (incrementalTime.isBefore(endTime)) {
    const startsAt = incrementalTime
    const endsAt = incrementalTime.clone().add(1, 'hours')

    // If we have multiple matches, that means this is a warehouse location with multiple zones
    const matches = staffingLevels.filter(
      ({ starts_at, ends_at }) =>
        moment(starts_at).isSame(startsAt) && moment(ends_at).isSame(endsAt)
    )

    // We will prefer non-0 allocation, as this identifies the primary zone.
    // https://instacart.atlassian.net/browse/CAT-14360
    const primaryZoneMatch = matches.find(
      ({ num_pickers, num_runners, num_full_service }) =>
        parseFloat(num_pickers) > 0 ||
        parseFloat(num_runners) > 0 ||
        parseFloat(num_full_service) > 0
    )

    // Fallback to first match to preserve existing behaviour
    const match = primaryZoneMatch || matches[0]

    const {
      num_pickers,
      dedicated_pickers,
      oncall_pickers,
      num_runners,
      num_full_service,
      min_staffing_hour,
    } = match || {}

    const scheduledPickers = getScheduledCount({
      shiftAllocations,
      startTime: startsAt,
      shiftType: ShiftType.Picking,
    })
    const requiredPickers = getRequiredCount(num_pickers)

    // Staffing levels for pickup shifts are calculated differently depending on whether device prioritization is enabled
    const pickingLevels: StaffingLevel[ShiftType.Picking] = useDevicePrioritization
      ? // if enabled, we separate both scheduled shifts and required staffing levels by dedicated/on call allocation type
        {
          [AllocationType.Dedicated]: {
            scheduled: getScheduledCount({
              shiftAllocations,
              startTime: startsAt,
              shiftType: ShiftType.Picking,
              shiftAllocationType: AllocationType.Dedicated,
            }),
            required: getRequiredCount(dedicated_pickers),
            closed: false,
          },
          [AllocationType.OnCall]: {
            scheduled: getScheduledCount({
              shiftAllocations,
              startTime: startsAt,
              shiftType: ShiftType.Picking,
              shiftAllocationType: AllocationType.OnCall,
            }),
            required: getRequiredCount(oncall_pickers),
            closed: false,
          },
        }
      : // if disabled, show levels in the OnCall Allocation row if min_staffing_hour is true
        {
          [AllocationType.Dedicated]: {
            scheduled: min_staffing_hour ? 0 : scheduledPickers,
            required: min_staffing_hour ? 0 : requiredPickers,
            closed: false,
          },
          [AllocationType.OnCall]: {
            scheduled: min_staffing_hour ? scheduledPickers : 0,
            required: min_staffing_hour ? requiredPickers : 0,
            closed: false,
          },
        }

    // Staffing levels for pickup shifts are calculated differently depending on whether isRunnerDevicePrioritizationEnabled is enabled
    const runningLevels: StaffingLevel[ShiftType.Running] = isRunnerDevicePrioritizationEnabled
      ? // if enabled, we separate both scheduled shifts and required staffing levels by dedicated/on call allocation type
        {
          [AllocationType.Dedicated]: {
            scheduled: getScheduledCount({
              shiftAllocations,
              startTime: startsAt,
              shiftType: ShiftType.Running,
              shiftAllocationType: AllocationType.Dedicated,
            }),
            required: getRequiredCount(num_runners),
            closed: false,
          },
          [AllocationType.OnCall]: {
            scheduled: getScheduledCount({
              shiftAllocations,
              startTime: startsAt,
              shiftType: ShiftType.Running,
              shiftAllocationType: AllocationType.OnCall,
            }),
            required: null, // there is no recommendation for on-call runners at this time
            closed: false,
          },
        }
      : // if disabled, show levels just for dedicated allocation
        {
          [AllocationType.Dedicated]: {
            scheduled: getScheduledCount({
              shiftAllocations,
              startTime: startsAt,
              shiftType: ShiftType.Running,
            }),
            required: getRequiredCount(num_runners),
            closed: false,
          },
          [AllocationType.OnCall]: {
            scheduled: 0,
            required: 0,
            closed: false,
          },
        }

    staffingAllocation.push({
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      [ShiftType.Picking]: pickingLevels,
      [ShiftType.Running]: runningLevels,
      [ShiftType.FullService]: {
        scheduled: getScheduledCount({
          shiftAllocations,
          startTime: startsAt,
          shiftType: ShiftType.FullService,
        }),
        required: getRequiredCount(num_full_service),
        closed: false,
      },
      [ShiftType.Driving]: {
        scheduled: getScheduledCount({
          shiftAllocations,
          startTime: startsAt,
          shiftType: ShiftType.Driving,
        }),
        required: getRequiredCount(num_full_service),
        closed: false,
      },
    })

    incrementalTime.add(1, 'hours')
  }

  return staffingAllocation
}

/* eslint-enable @typescript-eslint/naming-convention */
